import React, { useEffect, useState } from 'react';

const DepartmentModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialData = null, 
  saving = false,
  mode = 'create',
  onConfigure = null
}) => {
  const [name, setName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [error, setError] = useState(null);
  const [savedDepartment, setSavedDepartment] = useState(null);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '');
      setOriginalName(initialData.name || '');
    } else {
      setName('');
      setOriginalName('');
    }
    setError(null);
    setSavedDepartment(null);
  }, [initialData, isOpen]);

  const hasChanges = mode === 'edit' && name.trim() !== originalName.trim();
  const isValid = name.trim().length > 0;
  const canSave = mode === 'create' ? isValid : (isValid && hasChanges);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Department name is required.');
      return;
    }

    try {
      const result = await onSave({ name: trimmedName });
      setSavedDepartment(result || { name: trimmedName });

      if (mode === 'create') {
        setName('');
        setOriginalName('');
      } else {
        setOriginalName(trimmedName);
      }
    } catch (err) {
      setError(err?.message || 'Save failed. Please try again.');
    }
  };

  const handleInputChange = (value) => {
    setName(value);
    if (error) setError(null);
    if (savedDepartment) setSavedDepartment(null);
  };

  const handleConfigure = () => {
    if (savedDepartment && onConfigure) {
      onConfigure(savedDepartment);
    }
  };

  const getTitle = () => {
    if (savedDepartment) {
      return mode === 'create' ? 'Department Created' : 'Department Updated';
    }
    return mode === 'create' ? 'Create Department' : 'Edit Department';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4">
      {/* Modal Container: Acts as a bottom drawer on mobile, standard dialog on desktop */}
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-100 overflow-hidden transform transition-all max-h-[92vh] sm:max-h-none flex flex-col">
        
        {/* Mobile Drag Indicator / Top Notch */}
        <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto my-3 sm:hidden shrink-0" />

        {/* Modal Header */}
        <div className="px-5 pb-4 pt-2 sm:pt-5 border-b border-slate-100">
          <h2 className="text-base sm:text-lg font-bold text-slate-900">{getTitle()}</h2>
          <p className="text-slate-500 text-xs mt-1">
            {savedDepartment 
              ? mode === 'create' 
                ? 'Department configuration saved successfully.'
                : 'Changes saved successfully.'
              : mode === 'edit' && !hasChanges
                ? 'Modify the name field to push an update.'
                : 'Provide a structured name for the academic department.'
            }
          </p>
        </div>

        {/* Form Body Context */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4">
          
          {/* Success State Screen view */}
          {savedDepartment ? (
            <div className="space-y-4 py-1">
              <div className="p-3 bg-emerald-50 rounded-xl flex items-center gap-2.5 text-emerald-800 text-xs font-semibold">
                <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                <span>Changes processed into the ecosystem</span>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-500">Department Identifier</span>
                  <span className="text-slate-900 font-bold bg-white px-2.5 py-1 rounded-lg border border-slate-200">{savedDepartment.name}</span>
                </div>
                
                {savedDepartment.facultyIds && (
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-500">Allocated Staff Count</span>
                    <span className="text-slate-900 font-semibold">
                      {Array.isArray(savedDepartment.facultyIds) ? savedDepartment.facultyIds.length : 0} members
                    </span>
                  </div>
                )}
                
                <div className="pt-2 text-[10px] text-slate-400 font-medium text-center border-t border-dashed border-slate-200">
                  Timestamp: {formatTime(new Date())}
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:flex-1 py-3 px-4 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold text-xs sm:text-sm transition-colors"
                >
                  Dismiss Window
                </button>
                
                {onConfigure && (
                  <button
                    type="button"
                    onClick={handleConfigure}
                    className="w-full sm:flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold text-xs sm:text-sm transition-colors shadow-sm"
                  >
                    {mode === 'create' ? 'Configure Layout' : 'Inspect Profile'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            
            /* Core Action Input Form States */
            <>
              <div className="space-y-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
                    Department Nomenclature
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleInputChange(e.target.value)}
                    className="w-full px-3 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 text-sm bg-slate-50/50 transition-all font-medium"
                    placeholder="e.g. Computer Applications"
                    autoFocus
                  />
                </div>

                {/* Change Indicators / Form Validations */}
                {mode === 'edit' && (
                  <div className="flex items-center text-xs font-medium">
                    {hasChanges ? (
                      <div className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2.5 py-1 rounded-md">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        <span>Unsaved edits recognized</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-slate-400 px-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Synchronized with original parameter</span>
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-2 text-rose-700 text-xs font-medium">
                    <svg className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </div>

              {/* Layout Footer Actions */}
              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="w-full sm:flex-1 py-3 px-4 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold text-xs sm:text-sm transition-colors disabled:opacity-50"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  disabled={saving || !canSave}
                  className="w-full sm:flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed font-semibold text-xs sm:text-sm transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin h-3.5 w-3.5 text-current" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>
                      {mode === 'create' 
                        ? 'Create Unit' 
                        : hasChanges 
                          ? 'Commit Changes' 
                          : 'No Alterations'
                      }
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default DepartmentModal;